class FmFloatingIcon extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this._storageKey = this.getAttribute('data-storage-key') || 'fm-floating-icon-pos';
    this._dismissKey = this.getAttribute('data-dismiss-key');
    this._link = this.querySelector('.fm-floating-icon__link');
    this._mediaQuery = window.matchMedia('(max-width: 740px)');
    this._dragMoved = false;
    this._dragging = false;
    this._suppressClick = false;
    this._pointerId = null;
    this._dragThreshold = 8;

    if (this._isDismissed()) {
      this._hideDismissed();
      return;
    }

    this._onPointerDown = this._handlePointerDown.bind(this);
    this._onPointerMove = this._handlePointerMove.bind(this);
    this._onPointerUp = this._handlePointerUp.bind(this);
    this._onClickCapture = this._handleClickCapture.bind(this);
    this._onBreakpointChange = this._handleBreakpointChange.bind(this);

    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('click', this._onClickCapture, true);

    if (this._mediaQuery.addEventListener) {
      this._mediaQuery.addEventListener('change', this._onBreakpointChange);
    } else if (this._mediaQuery.addListener) {
      this._mediaQuery.addListener(this._onBreakpointChange);
    }

    this._applyStoredOrDefault({ animate: false });
    this.classList.add('is-positioned');
  }

  disconnectedCallback() {
    this._unbindDragListeners();

    if (this._mediaQuery) {
      if (this._mediaQuery.removeEventListener) {
        this._mediaQuery.removeEventListener('change', this._onBreakpointChange);
      } else if (this._mediaQuery.removeListener) {
        this._mediaQuery.removeListener(this._onBreakpointChange);
      }
    }
  }

  _isMobile() {
    return this._mediaQuery.matches;
  }

  _isDismissed() {
    if (!this._dismissKey) return false;
    try {
      return sessionStorage.getItem(this._dismissKey) === '1';
    } catch (err) {
      return false;
    }
  }

  _saveDismissed() {
    if (!this._dismissKey) return;
    try {
      sessionStorage.setItem(this._dismissKey, '1');
    } catch (err) {}
  }

  _hideDismissed() {
    this.classList.add('is-dismissed');
    this.setAttribute('hidden', '');
    this.remove();
  }

  _dismiss() {
    this._saveDismissed();
    this._hideDismissed();
  }

  _blurInternalFocus() {
    const active = document.activeElement;
    if (active && this.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }
  }

  _readStorage() {
    try {
      const raw = sessionStorage.getItem(this._storageKey) || localStorage.getItem(this._storageKey);
      return JSON.parse(raw || 'null') || {};
    } catch (err) {
      return {};
    }
  }

  _writeStorage(next) {
    const value = JSON.stringify(next);
    try {
      sessionStorage.setItem(this._storageKey, value);
    } catch (err) {}
    try {
      localStorage.setItem(this._storageKey, value);
    } catch (err) {}
  }

  _getEdgeGap() {
    const styles = getComputedStyle(this);
    const value = parseFloat(styles.getPropertyValue('--fm-fi-edge'));
    return Number.isFinite(value) ? value : 8;
  }

  _applySide(side) {
    this.setAttribute('data-side', side === 'left' ? 'left' : 'right');
  }

  _applyBottom(bottom) {
    const clamped = this._clampBottom(bottom);
    this.style.setProperty('--fm-fi-bottom', clamped + 'px');
    this.style.bottom = clamped + 'px';
    this.style.top = 'auto';
    return clamped;
  }

  _clampBottom(bottom) {
    const height = this.offsetHeight || 0;
    const max = Math.max(0, window.innerHeight - height);
    const value = Number(bottom);
    return Math.max(0, Math.min(Number.isFinite(value) ? value : 0, max));
  }

  _clearDragInline() {
    this.style.left = '';
    this.style.right = '';
    this.style.top = '';
  }

  _defaultSide() {
    const attr = this._isMobile() ? 'data-side-mobile' : 'data-side-desktop';
    const side = this.getAttribute(attr);
    return side === 'left' ? 'left' : 'right';
  }

  _normalizePos(pos) {
    if (!pos || (pos.s !== 'left' && pos.s !== 'right')) return null;
    const bottom = Number(pos.b);
    if (!Number.isFinite(bottom)) return null;
    return { s: pos.s, b: bottom };
  }

  _applyStoredOrDefault({ animate }) {
    const data = this._readStorage();
    const pos = this._normalizePos(this._isMobile() ? data.m : data.d);

    this.classList.toggle('is-snapping', Boolean(animate));

    if (pos) {
      this._applySide(pos.s);
      this._clearDragInline();
      this._applyBottom(pos.b);
      this.dataset.restored = 'true';
    } else if (!this.dataset.restored) {
      this._applySide(this._defaultSide());
      this._clearDragInline();
      this.style.removeProperty('--fm-fi-bottom');
      this.style.bottom = '';
      this.style.top = 'auto';
    }

    if (animate) {
      window.setTimeout(() => this.classList.remove('is-snapping'), 240);
    }
  }

  _savePosition(side, bottom) {
    const data = this._readStorage();
    const key = this._isMobile() ? 'm' : 'd';
    data[key] = { s: side, b: this._clampBottom(bottom) };
    this._writeStorage(data);
    this.dataset.restored = 'true';
  }

  _handleBreakpointChange() {
    this._applyStoredOrDefault({ animate: false });
  }

  _handlePointerDown(event) {
    if (event.button != null && event.button !== 0) return;

    if (event.target.closest('[data-fm-floating-dismiss]')) {
      event.preventDefault();
      event.stopPropagation();
      this._dismiss();
      return;
    }

    if (!event.target.closest('.fm-floating-icon__link') && event.target !== this) return;

    this._dragMoved = false;
    this._suppressClick = false;
    this._dragging = true;
    this._pointerId = event.pointerId;
    this._startX = event.clientX;
    this._startY = event.clientY;

    const rect = this.getBoundingClientRect();
    this._offsetX = event.clientX - rect.left;
    this._offsetY = event.clientY - rect.top;

    document.addEventListener('pointermove', this._onPointerMove, { passive: false });
    document.addEventListener('pointerup', this._onPointerUp);
    document.addEventListener('pointercancel', this._onPointerUp);
  }

  _beginDrag(event) {
    this._dragMoved = true;
    this._suppressClick = true;
    this.classList.add('is-dragging');
    this.classList.remove('is-snapping');

    try {
      this.setPointerCapture(event.pointerId);
    } catch (err) {}
  }

  _handlePointerMove(event) {
    if (!this._dragging || this._pointerId !== event.pointerId) return;

    const dx = event.clientX - this._startX;
    const dy = event.clientY - this._startY;

    if (!this._dragMoved) {
      if (Math.hypot(dx, dy) < this._dragThreshold) return;
      this._beginDrag(event);
    }

    event.preventDefault();

    const width = this.offsetWidth;
    const height = this.offsetHeight;
    const maxLeft = Math.max(0, window.innerWidth - width);
    const maxTop = Math.max(0, window.innerHeight - height);
    const left = Math.max(0, Math.min(event.clientX - this._offsetX, maxLeft));
    const top = Math.max(0, Math.min(event.clientY - this._offsetY, maxTop));

    this.style.left = left + 'px';
    this.style.right = 'auto';
    this.style.top = top + 'px';
    this.style.bottom = 'auto';
  }

  _handlePointerUp(event) {
    if (!this._dragging || this._pointerId !== event.pointerId) return;

    const didDrag = this._dragMoved;
    this._dragging = false;
    this._unbindDragListeners();
    this.classList.remove('is-dragging');

    try {
      this.releasePointerCapture(event.pointerId);
    } catch (err) {}

    this._pointerId = null;
    this._blurInternalFocus();

    if (!didDrag) return;

    const rect = this.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const side = midX < window.innerWidth / 2 ? 'left' : 'right';
    const bottom = window.innerHeight - rect.bottom;
    const edge = this._getEdgeGap();
    // Animate via `left` for both sides. Switching to `right` mid-drag cannot
    // transition (left/right jump between px and auto), which looked like a flash.
    const targetLeft = side === 'left' ? edge : Math.max(0, window.innerWidth - rect.width - edge);

    this.classList.add('is-snapping');
    this._applySide(side);
    this.style.right = 'auto';
    this.style.left = targetLeft + 'px';
    this.style.top = 'auto';
    const clampedBottom = this._applyBottom(bottom);
    this._savePosition(side, clampedBottom);

    window.setTimeout(() => {
      this._clearDragInline();
      this.classList.remove('is-snapping');
      this._blurInternalFocus();
    }, 240);
  }

  _unbindDragListeners() {
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup', this._onPointerUp);
    document.removeEventListener('pointercancel', this._onPointerUp);
  }

  _handleClickCapture(event) {
    if (event.target.closest('[data-fm-floating-dismiss]')) {
      event.preventDefault();
      event.stopPropagation();
      // Dismiss is handled on pointerdown; block any leftover click/navigation.
      return;
    }

    if (this._suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
      this._dragMoved = false;
      return;
    }

    if (this.getAttribute('data-open-spin-wheel') === 'true') {
      event.preventDefault();
      event.stopPropagation();
      const popup = document.querySelector('subscription-popup');
      if (popup && typeof popup.showSpinWheel === 'function') {
        popup.showSpinWheel();
      }
      return;
    }

    const href = this._link?.getAttribute('href');
    if (!href || href === '#') {
      event.preventDefault();
    }
  }
}


if (!customElements.get('fm-floating-icon')) {
  customElements.define('fm-floating-icon', FmFloatingIcon);
}
